import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { isNative } from '@/lib/platform';
import { startRain } from '@/native/media/rainSource';

// Royalty-free rain sound
const RAIN_SOUND_URL = 'https://www.soundjay.com/nature/sounds/rain-07.mp3';

/**
 * Rain ambience.
 *
 * WEB (unchanged): streams the mp3 above into an HTMLAudioElement after the
 * first user gesture.
 *
 * NATIVE: the mp3 path is wrong inside a shipping binary for three reasons. It
 * makes a third-party host a runtime dependency of an app that is otherwise
 * self-contained, so the feature dies offline and on a flaky connection. It adds
 * a domain to the app's network profile for a decorative feature. And an
 * `<audio>` element plays into the iOS "ambient" audio session, which the
 * hardware ring switch silences outright and which fights the play-and-record
 * category that `audioUnlock.js` manages for the microphone. So on native the
 * rain is synthesised inside the AudioContext `audioUnlock` already owns: no
 * network, no second audio session, and it survives the ring switch.
 *
 * The native branch is behind `isNative()`, which is false in every browser
 * including the installed PWA, so the web path is untouched.
 */
export const useRainSound = () => {
  const { rainSoundEnabled, rainVolume } = useAccessibility();
  const audioRef = useRef(null);
  const nativeRainRef = useRef(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    const handleInteraction = () => {
      setHasInteracted(true);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('click', handleInteraction);
    window.addEventListener('keydown', handleInteraction);

    return () => {
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, []);

  // Create and clean up audio element
  useEffect(() => {
    if (isNative()) return undefined; // native uses the WebAudio source below
    if (!audioRef.current) {
      audioRef.current = new Audio(RAIN_SOUND_URL);
      audioRef.current.loop = true;
      audioRef.current.volume = rainVolume / 100;
    }
    const audio = audioRef.current;
    // Pause when tab is hidden
    const handleVisibility = () => {
      if (document.hidden) {
        audio.pause();
      } else if (rainSoundEnabled && hasInteracted) {
        audio.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audioRef.current = null;
    };
  }, []);

  // Play/pause based on enabled and interaction
  useEffect(() => {
    if (isNative()) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (rainSoundEnabled && hasInteracted) {
      audio.play().catch(error => {
        console.error('Rain sound autoplay was prevented:', error);
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [rainSoundEnabled, hasInteracted]);

  useEffect(() => {
    if (isNative()) return;
    if (audioRef.current) {
      audioRef.current.volume = rainVolume / 100;
    }
  }, [rainVolume]);

  /* ---------------------------- native only ---------------------------- */

  // Start and stop the synthesised rain. `hasInteracted` still gates it: WebAudio
  // needs the same gesture, and audioUnlock's context is only running once the
  // user has tapped something.
  useEffect(() => {
    if (!isNative()) return undefined;

    let cancelled = false;

    const stopNative = () => {
      try {
        nativeRainRef.current?.stop();
      } catch {
        /* non-fatal */
      }
      nativeRainRef.current = null;
    };

    if (rainSoundEnabled && hasInteracted) {
      if (!nativeRainRef.current) {
        startRain({ volume: rainVolume / 100 })
          .then((handle) => {
            if (cancelled || !handle) {
              handle?.stop();
              return;
            }
            nativeRainRef.current = handle;
          })
          .catch(() => {
            /* rain is decorative: never surface a failure */
          });
      }
    } else {
      stopNative();
    }

    return () => {
      cancelled = true;
    };
  }, [rainSoundEnabled, hasInteracted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNative()) return;
    nativeRainRef.current?.setVolume(rainVolume / 100);
  }, [rainVolume]);

  useEffect(() => () => {
    try {
      nativeRainRef.current?.stop();
    } catch {
      /* non-fatal */
    }
    nativeRainRef.current = null;
  }, []);

  return null;
};
